import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Wifi, Check, Loader2, RefreshCw, Search, Sparkles, Clock, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * WelcomeModal - Premium Glassmorphism Welcome Cards
 * 
 * Flow for NEW users:
 * - Window 1: Activation - Request GPS location
 * - Window 2: Success - Show city name with calculated prayer times (FREE)
 * - Note at bottom: "For more accuracy, sync with your mosque" → Pro feature
 * 
 * Dimensions: 320px width, ~200px height, 24px border-radius
 * Visual: Glassmorphism (20px blur), 1px white border
 */
const WelcomeModal = ({ isOpen, onClose, onComplete }) => {
  const { t } = useTranslation();
  // States: 'initial', 'syncing', 'success', 'manual'
  const [state, setState] = useState('initial');
  const [cityName, setCityName] = useState('');
  const [syncData, setSyncData] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const cardStyle = {
    width: '320px',
    minHeight: '200px',
    borderRadius: '24px',
  };

  const handleSync = async () => {
    setState('syncing');
    setStatusMessage(t('welcome.detectingLocation', 'Detecting your location...'));

    try {
      // Get GPS location
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;
      
      // Step 1: Get city name via reverse geocoding
      setStatusMessage(t('welcome.findingCity', 'Finding your city...'));
      let city = '';
      let country = '';
      
      try {
        const geoResponse = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
        );
        
        if (geoResponse.ok) {
          const geoData = await geoResponse.json();
          city = geoData.address?.city || geoData.address?.town || geoData.address?.village || geoData.address?.municipality || '';
          country = geoData.address?.country || '';
        }
      } catch (e) {
        console.log('Reverse geocoding failed:', e.message);
      }
      
      // Step 2: Get calculated prayer times using astronomical calculations
      setStatusMessage(t('welcome.calculatingTimes', 'Calculating prayer times...'));
      
      try {
        const prayerResponse = await fetch(
          `${API_URL}/api/prayer-times?latitude=${latitude}&longitude=${longitude}`
        );
        
        if (prayerResponse.ok) {
          const prayerData = await prayerResponse.json();
          
          // Build result with city name and calculated times
          const result = {
            source: 'calculated',
            cityName: city,
            country: country,
            calculationMethod: prayerData.method || 'MWL',
            startTimes: {
              fajr: prayerData.times?.Fajr || prayerData.times?.fajr,
              sunrise: prayerData.times?.Sunrise || prayerData.times?.sunrise,
              dhuhr: prayerData.times?.Dhuhr || prayerData.times?.dhuhr,
              asr: prayerData.times?.Asr || prayerData.times?.asr,
              maghrib: prayerData.times?.Maghrib || prayerData.times?.maghrib,
              isha: prayerData.times?.Isha || prayerData.times?.isha,
            },
            confidence: 'calculated'
          };
          
          // SUCCESS - Show success window with city name
          setCityName(city || t('welcome.yourLocation', 'Your Location'));
          setSyncData(result);
          setState('success');
          
          // Save to localStorage - Prayer Times page will use this
          localStorage.setItem('ustadSyncData', JSON.stringify({
            ...result,
            syncedAt: new Date().toISOString(),
            coordinates: { lat: latitude, lon: longitude }
          }));
          
          // Save user's location for Prayer Times page
          localStorage.setItem('quranplus-user-location', JSON.stringify({
            latitude,
            longitude,
            city,
            country,
            timestamp: new Date().toISOString()
          }));
          
          localStorage.setItem('quranplus-welcome-completed', 'true');
          return;
        }
      } catch (e) {
        console.log('Prayer times calculation failed:', e.message);
      }
      
      // Fallback: If calculation failed, still show success with city name
      if (city) {
        setCityName(city);
        setSyncData({
          source: 'location_only',
          cityName: city,
          country: country,
          coordinates: { lat: latitude, lon: longitude }
        });
        setState('success');
        
        localStorage.setItem('quranplus-user-location', JSON.stringify({
          latitude,
          longitude,
          city,
          country,
          timestamp: new Date().toISOString()
        }));
        
        localStorage.setItem('quranplus-welcome-completed', 'true');
      } else {
        // No city found - Show manual setup
        setState('manual');
        localStorage.setItem('quranplus-welcome-completed', 'true');
      }

    } catch (err) {
      console.log('Sync process:', err.message);
      // GPS failed or other error - Show positive manual setup window
      setState('manual');
      localStorage.setItem('quranplus-welcome-completed', 'true');
    }
  };

  const handleContinue = () => {
    if (onComplete) onComplete(syncData);
    if (onClose) onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('quranplus-welcome-completed', 'true');
    if (onComplete) onComplete(null);
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-emerald-900/90 via-teal-900/90 to-cyan-900/90"
      data-testid="welcome-modal"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl" />
      </div>

      <AnimatePresence mode="wait">
        {/* Window 1: Activation */}
        {(state === 'initial' || state === 'syncing') && (
          <motion.div
            key="activation"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            style={cardStyle}
            className="relative overflow-hidden"
            data-testid="welcome-activation-card"
          >
            <div 
              className="absolute inset-0 bg-white/10 border border-white/20"
              style={{
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRadius: '24px',
              }}
            />
            
            <div className="relative z-10 h-full flex flex-col items-center justify-center p-6 text-center">
              <motion.div
                animate={state === 'syncing' ? { rotate: 360 } : { 
                  boxShadow: [
                    '0 0 0 0 rgba(16, 185, 129, 0.4)',
                    '0 0 0 15px rgba(16, 185, 129, 0)',
                    '0 0 0 0 rgba(16, 185, 129, 0)'
                  ]
                }}
                transition={state === 'syncing' ? { duration: 2, repeat: Infinity, ease: "linear" } : { duration: 2, repeat: Infinity }}
                className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30"
              >
                {state === 'syncing' ? (
                  <RefreshCw className="w-7 h-7 text-white" />
                ) : (
                  <MapPin className="w-7 h-7 text-white" />
                )}
              </motion.div>

              <h3 className="text-xl font-semibold text-white mb-2">
                {t('welcome.activateTitle', 'Set Your Location')}
              </h3>

              <p className="text-sm text-white/80 leading-relaxed mb-5 px-2">
                {state === 'syncing' 
                  ? statusMessage
                  : t('welcome.activateBody', "Enable location to get accurate prayer times for your city.")}
              </p>

              <div className="flex flex-col gap-2 w-full px-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSync}
                  disabled={state === 'syncing'}
                  className={`w-full py-3 rounded-full text-sm font-semibold transition-all duration-200 shadow-lg ${
                    state === 'syncing' 
                      ? 'bg-white/20 text-white/70 cursor-wait' 
                      : 'bg-gradient-to-r from-emerald-400 to-teal-400 hover:from-emerald-500 hover:to-teal-500 text-white shadow-emerald-500/30'
                  }`}
                  data-testid="sync-mosque-btn"
                >
                  {state === 'syncing' ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('welcome.syncing', 'Setting up...')}
                    </span>
                  ) : (
                    t('welcome.syncButton', 'Set My Location')
                  )}
                </motion.button>
                
                {state === 'initial' && (
                  <button
                    onClick={handleSkip}
                    className="text-sm text-white/60 hover:text-white/80 transition-colors py-2"
                    data-testid="skip-btn"
                  >
                    {t('common.skip', 'Skip')}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Window 2a: Success */}
        {state === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            style={{ ...cardStyle, minHeight: '240px' }}
            className="relative overflow-hidden"
            data-testid="welcome-success-card"
          >
            <div 
              className="absolute inset-0 bg-white/10 border border-white/20"
              style={{
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRadius: '24px',
                boxShadow: '0 0 60px rgba(16, 185, 129, 0.3)'
              }}
            />
            
            <div className="relative z-10 h-full flex flex-col items-center justify-center p-6 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ 
                  scale: 1,
                  boxShadow: [
                    '0 0 20px rgba(16, 185, 129, 0.5)',
                    '0 0 40px rgba(16, 185, 129, 0.7)',
                    '0 0 20px rgba(16, 185, 129, 0.5)'
                  ]
                }}
                transition={{ scale: { duration: 0.3, ease: "backOut" }, boxShadow: { duration: 2, repeat: Infinity } }}
                className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center mb-4"
              >
                <Check className="w-8 h-8 text-white" strokeWidth={3} />
              </motion.div>

              <h3 className="text-xl font-semibold text-white mb-2">
                {t('welcome.locationSet', 'Location Set!')}
              </h3>

              <p className="text-sm text-white/80 leading-relaxed mb-1">
                {t('welcome.prayerTimesConfigured', 'Prayer times configured for')}
              </p>
              <p className="text-base font-semibold text-emerald-300 mb-2 px-4 truncate max-w-full">
                {cityName}
              </p>

              <div className="flex items-center gap-1.5 text-xs text-emerald-300 mb-3">
                <Clock className="w-3.5 h-3.5" />
                <span>{t('welcome.calculatedTimes', 'Calculated Prayer Times')}</span>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleContinue}
                className="w-full max-w-[200px] py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-emerald-400 to-green-400 hover:from-emerald-500 hover:to-green-500 text-white shadow-lg shadow-emerald-500/30 transition-all duration-200 mb-3"
                data-testid="continue-btn"
              >
                {t('welcome.continue', 'Continue')}
              </motion.button>
              
              {/* Pro teaser for mosque sync */}
              <p className="text-[11px] text-white/50 flex items-center gap-1">
                <Crown className="w-3 h-3 text-amber-400/70" />
                {t('welcome.moreAccuracyHint', 'For more accuracy, sync with your mosque')}
              </p>
            </div>
          </motion.div>
        )}

        {/* Window 2b: Manual Setup (Positive) */}
        {state === 'manual' && (
          <motion.div
            key="manual"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            style={cardStyle}
            className="relative overflow-hidden"
            data-testid="welcome-manual-card"
          >
            <div 
              className="absolute inset-0 bg-white/10 border border-white/20"
              style={{
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRadius: '24px',
                boxShadow: '0 0 40px rgba(59, 130, 246, 0.2)'
              }}
            />
            
            <div className="relative z-10 h-full flex flex-col items-center justify-center p-6 text-center">
              <motion.div
                animate={{ 
                  boxShadow: [
                    '0 0 15px rgba(59, 130, 246, 0.4)',
                    '0 0 25px rgba(59, 130, 246, 0.6)',
                    '0 0 15px rgba(59, 130, 246, 0.4)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mb-4 shadow-lg"
              >
                <Sparkles className="w-7 h-7 text-white" />
              </motion.div>

              <h3 className="text-xl font-semibold text-white mb-2">
                {t('welcome.welcomeTitle', "You're All Set!")}
              </h3>

              <p className="text-sm text-white/80 leading-relaxed mb-2 px-2">
                {t('welcome.manualSetupBody', 'Search for your mosque anytime in Prayer Times to get live Jamat updates.')}
              </p>

              <div className="flex items-center gap-1.5 text-xs text-blue-300 mb-4">
                <Search className="w-3.5 h-3.5" />
                <span>{t('welcome.searchAnytime', 'Search anytime')}</span>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleContinue}
                className="w-full max-w-[200px] py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-400 to-indigo-400 hover:from-blue-500 hover:to-indigo-500 text-white shadow-lg shadow-blue-500/30 transition-all duration-200"
                data-testid="continue-btn"
              >
                {t('welcome.getStarted', 'Get Started')}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WelcomeModal;
